import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import JsonLd from '../components/JsonLd';
import { SITE_URL, SITE_NAME, DEFAULT_IMG } from '../components/SEOHead';

const API = `${window.location.origin}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(r => { setPost(r.data); setLoading(false); })
      .catch(err => { setNotFound(err.response?.status === 404); setLoading(false); });
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><p className="text-white/50 font-sans text-sm">Loading…</p></div>;
  if (notFound || !post) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <SEOHead title="Article Not Found" noindex />
        <div className="text-center px-4">
          <h1 className="text-4xl text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Article not found</h1>
          <Link to="/blog" className="text-[#D4AF37] underline text-sm font-sans">Back to all articles</Link>
        </div>
      </main>
    );
  }

  const cover = post.cover_image || DEFAULT_IMG;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: cover.startsWith('http') ? cover : `${SITE_URL}${cover}`,
    author: { '@type': 'Organization', name: post.author || SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/icons/icon-512x512.png` },
    },
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
    keywords: (post.tags || []).join(', '),
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title={post.title}
        description={post.excerpt}
        image={cover.startsWith('http') ? cover : `${SITE_URL}${cover}`}
        url={`${SITE_URL}/blog/${post.slug}`}
        type="article"
      />
      <JsonLd id="article" data={articleSchema} />
      <BreadcrumbSchema crumbs={[
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: post.title, path: `/blog/${post.slug}` },
      ]} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-xs uppercase tracking-[0.18em] font-sans mb-6">
          <ArrowLeft className="w-3 h-3" /> All Articles
        </Link>

        <header className="mb-8">
          {post.tags && post.tags[0] && (
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-sans mb-3">{post.tags[0]}</p>
          )}
          <h1 className="text-4xl sm:text-5xl text-white font-light leading-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {post.title}
          </h1>
          <div className="text-white/40 text-xs font-sans flex items-center gap-3">
            <span>{post.author || SITE_NAME}</span>
            <span>·</span>
            <time dateTime={post.published_at || post.created_at}>
              {new Date(post.published_at || post.created_at).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
          </div>
        </header>

        {post.cover_image && (
          <div className="aspect-[16/10] overflow-hidden mb-10 bg-[#141414]">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="prose prose-invert max-w-none prose-headings:font-serif prose-headings:text-white prose-p:text-white/70 prose-a:text-[#D4AF37] prose-strong:text-white prose-li:text-white/70"
          dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
        />
      </article>
    </main>
  );
}
